import {
  FC,
  useRef,
  useEffect,
  useCallback,
  KeyboardEvent as ReactKeyboardEvent,
} from 'react';
import { NavLink } from 'react-router-dom';
import styles from './NavDropdown.module.scss';

export interface DropdownItem {
  label: string;
  to: string;
}

export interface NavDropdownProps {
  label: string;
  items: DropdownItem[];
  isOpen: boolean;
  onToggle: () => void;
  onClose: () => void;
  onOpen: () => void;
  isActive?: boolean;
  variant?: "dark" | "light";
}

const NavDropdown: FC<NavDropdownProps> = ({
  label,
  items,
  isOpen,
  onToggle,
  onClose,
  onOpen,
  isActive = false,
  variant = "dark",
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLUListElement>(null);

  const menuId = `dropdown-menu-${label.toLowerCase().replace(/\s+/g, '-')}`;

  // Close when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  // Handle escape key
  const handleKeyDown = useCallback(
    (event: ReactKeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        event.preventDefault();
        onClose();
        triggerRef.current?.focus();
      } else if (event.key === 'ArrowDown' && !isOpen) {
        event.preventDefault();
        onOpen();
      }
    },
    [isOpen, onClose, onOpen]
  );

  // Desktop hover interactions
  const handleMouseEnter = () => {
    onOpen();
  };

  const handleMouseLeave = () => {
    onClose();
  };

  return (
    <div
      ref={containerRef}
      className={styles.dropdownWrapper}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onKeyDown={handleKeyDown}
    >
      <button
        ref={triggerRef}
        type="button"
        className={`${styles.trigger} ${
          variant === "light" ? styles.lightVariant : ""
        } ${isOpen ? styles.open : ""} ${
          isActive ? styles.active : ""
        }`}
        onClick={onToggle}
        aria-expanded={isOpen}
        aria-haspopup="true"
        aria-controls={menuId}
      >
        <span>{label}</span>
        <svg
          className={styles.chevron}
          viewBox="0 0 12 12"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
        >
          <path
            d="M2.5 4.5L6 8L9.5 4.5"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {isOpen && (
        <ul
          id={menuId}
          ref={menuRef}
          className={styles.menu}
          role="menu"
          aria-label={`${label} sub-navigation`}
        >
          {items.map((item) => (
            <li key={item.to} className={styles.menuItem} role="none">
              <NavLink
                to={item.to}
                role="menuitem"
                onClick={onClose}
                className={({ isActive: itemActive }) =>
                  `${styles.menuLink} ${itemActive ? styles.activeItem : ''}`
                }
              >
                {item.label}
              </NavLink>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default NavDropdown;
